import {
    SORT_PATH_FILL_ALGORITHMS,
    SORT_PATH_PICK_ALGORITHMS,
    SORT_PATH_SETTING_STATUS,
    SortingOrder,
} from "../Constants";
import {
    CSV_HEADERS,
    CLUSTER_PARAMS_HEADERS,
    VALID_SPLIT_PERCENTAGES,
    VALID_COLUMN_LENGTHS,
    VALID_ADTA_ORDERS,
    MAX_NAME_LENGTH,
    MAX_SIDELINE_SORT_ZONES,
} from "../constants/BulkUploadConstants";

const VALID_FILL_ALGORITHMS = Object.values(SORT_PATH_FILL_ALGORITHMS);
const VALID_PICK_ALGORITHMS = Object.values(SORT_PATH_PICK_ALGORITHMS);

const VALID_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING, SortingOrder.CUSTOM, "DIVERTER_COUNT"];
const VALID_AISLE_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING, SortingOrder.CUSTOM];
const VALID_PICK_AISLE_ORDER_TYPES = [SortingOrder.INCREASING, SortingOrder.DECREASING];

/**
 * Parses a CSV string into an array of row objects keyed by header names.
 */
function buildRowFromHeaders(headers, values) {
    var row = {};
    headers.forEach(function(header, idx) {
        row[header] = values[idx] || "";
    });
    return row;
}

export function parseCSV(csvText) {
    const lines = csvText.split(/\r?\n/).filter(function(line) { return line.trim().length > 0; });
    if (lines.length < 2) {
        return { error: "CSV must have a header row and at least one data row" };
    }

    const headers = lines[0].split(",").map(function(h) { return h.trim(); });
    const missingHeaders = [CSV_HEADERS.NODE_ID, CSV_HEADERS.CYCLE, CSV_HEADERS.FILL_ALGORITHM, CSV_HEADERS.PICK_ALGORITHM]
        .filter(function(rh) { return !headers.includes(rh); });
    if (missingHeaders.length > 0) {
        return { error: "Missing required CSV headers: " + missingHeaders.join(", ") };
    }

    var rows = [];
    for (var i = 1; i < lines.length; i++) {
        var values = lines[i].split(",").map(function(v) { return v.trim(); });
        var row = buildRowFromHeaders(headers, values);
        row._rowNumber = i;
        rows.push(row);
    }
    return { rows: rows };
}

/**
 * Parses the cluster parameters CSV into a map keyed by "nodeId#cycle".
 */
export function parseClusterParamsCSV(csvText) {
    var lines = csvText.split(/\r?\n/).filter(function(line) { return line.trim().length > 0; });
    if (lines.length < 2) {
        return { error: "Cluster params CSV must have a header row and at least one data row" };
    }

    var headers = lines[0].split(",").map(function(h) { return h.trim(); });
    var missingHeaders = [CLUSTER_PARAMS_HEADERS.NODE_ID, CLUSTER_PARAMS_HEADERS.CYCLE, CLUSTER_PARAMS_HEADERS.CLUSTER_NAME]
        .filter(function(rh) { return !headers.includes(rh); });
    if (missingHeaders.length > 0) {
        return { error: "Cluster params CSV missing required headers: " + missingHeaders.join(", ") };
    }

    var clusterParamsMap = {};
    for (var i = 1; i < lines.length; i++) {
        var values = lines[i].split(",").map(function(v) { return v.trim(); });
        var row = buildRowFromHeaders(headers, values);

        var key = row[CLUSTER_PARAMS_HEADERS.NODE_ID] + "#" + row[CLUSTER_PARAMS_HEADERS.CYCLE];
        if (!clusterParamsMap[key]) {
            clusterParamsMap[key] = [];
        }
        var adtaVal = row[CLUSTER_PARAMS_HEADERS.ADTA_ENABLED] || "";
        clusterParamsMap[key].push({
            clusterName: row[CLUSTER_PARAMS_HEADERS.CLUSTER_NAME],
            adtaEnabled: adtaVal.toUpperCase() === "TRUE",
            blockLength: row[CLUSTER_PARAMS_HEADERS.BLOCK_LENGTH] ? parseInt(row[CLUSTER_PARAMS_HEADERS.BLOCK_LENGTH], 10) : null,
            maxCapacity: row[CLUSTER_PARAMS_HEADERS.MAX_CAPACITY] ? parseInt(row[CLUSTER_PARAMS_HEADERS.MAX_CAPACITY], 10) : null,
        });
    }
    return { clusterParamsMap: clusterParamsMap };
}

/**
 * Validates a single parsed CSV row.
 */
export function validateRow(row, clusterParamsMap, existingConfigs) {
    // Required fields
    var missing = [CSV_HEADERS.NODE_ID, CSV_HEADERS.CYCLE, CSV_HEADERS.FILL_ALGORITHM, CSV_HEADERS.PICK_ALGORITHM]
        .filter(function(h) { return !row[h]; });
    if (missing.length > 0) {
        return "Row " + row._rowNumber + ": Missing required fields: " + missing.join(", ");
    }

    // Name auto-generation and length check
    var name = row[CSV_HEADERS.CYCLE] + "_" + row[CSV_HEADERS.FILL_ALGORITHM];
    if (name.length > MAX_NAME_LENGTH) {
        name = row[CSV_HEADERS.CYCLE];
    }
    if (name.length > MAX_NAME_LENGTH) {
        return "Row " + row._rowNumber + ": Auto-generated name \"" + name + "\" exceeds " + MAX_NAME_LENGTH + " character limit";
    }

    // Name conflict for CREATE
    if (existingConfigs && existingConfigs.length > 0) {
        var hasExistingForCycle = existingConfigs.some(function(c) {
            return c.nodeId === row[CSV_HEADERS.NODE_ID] && c.cycle === row[CSV_HEADERS.CYCLE];
        });
        if (!hasExistingForCycle) {
            var nameConflict = existingConfigs.some(function(c) { return c.name === name; });
            if (nameConflict) {
                return "Row " + row._rowNumber + ": Name \"" + name + "\" already exists.";
            }
        }
    }

    // Fill algorithm validation
    if (!VALID_FILL_ALGORITHMS.includes(row[CSV_HEADERS.FILL_ALGORITHM])) {
        return "Row " + row._rowNumber + ": Invalid fillPathAlgorithm \"" + row[CSV_HEADERS.FILL_ALGORITHM] + "\"";
    }

    // Pick algorithm validation
    if (!VALID_PICK_ALGORITHMS.includes(row[CSV_HEADERS.PICK_ALGORITHM])) {
        return "Row " + row._rowNumber + ": Invalid pickPathAlgorithm \"" + row[CSV_HEADERS.PICK_ALGORITHM] + "\"";
    }

    // Order type validations
    var fillAlgo = row[CSV_HEADERS.FILL_ALGORITHM];
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.COMBINATRIX) {
        if (row[CSV_HEADERS.FILL_FINGER_ORDER] && !VALID_AISLE_ORDER_TYPES.includes(row[CSV_HEADERS.FILL_FINGER_ORDER])) {
            return "Row " + row._rowNumber + ": Invalid fill_finger_order \"" + row[CSV_HEADERS.FILL_FINGER_ORDER] + "\"";
        }
        if (row[CSV_HEADERS.FILL_SPLIT_PERCENTAGE] && !VALID_SPLIT_PERCENTAGES.includes(row[CSV_HEADERS.FILL_SPLIT_PERCENTAGE])) {
            return "Row " + row._rowNumber + ": Invalid fill_split_percentage \"" + row[CSV_HEADERS.FILL_SPLIT_PERCENTAGE] + "\"";
        }
    }

    if (row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] && !VALID_ORDER_TYPES.includes(row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE])) {
        return "Row " + row._rowNumber + ": Invalid fill_cluster_order_type \"" + row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] + "\"";
    }

    if (row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] && !VALID_AISLE_ORDER_TYPES.includes(row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE])) {
        return "Row " + row._rowNumber + ": Invalid fill_aisle_order_type \"" + row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] + "\"";
    }

    if (row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] && !VALID_AISLE_ORDER_TYPES.includes(row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE])) {
        return "Row " + row._rowNumber + ": Invalid pick_cluster_order_type \"" + row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] + "\"";
    }

    if (row[CSV_HEADERS.PICK_AISLE_ORDER_TYPE] && !VALID_PICK_AISLE_ORDER_TYPES.includes(row[CSV_HEADERS.PICK_AISLE_ORDER_TYPE])) {
        return "Row " + row._rowNumber + ": Invalid pick_aisle_order_type \"" + row[CSV_HEADERS.PICK_AISLE_ORDER_TYPE] + "\"";
    }

    if (row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] && !VALID_ADTA_ORDERS.includes(row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER])) {
        return "Row " + row._rowNumber + ": Invalid fill_adta_prioritization_order \"" + row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] + "\"";
    }

    // Partial column zebra validation
    if (row[CSV_HEADERS.PICK_ALGORITHM] === SORT_PATH_PICK_ALGORITHMS.PARTIAL_COLUMN_ZEBRA_PATTERN) {
        if (!row[CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA]) {
            return "Row " + row._rowNumber + ": pick_column_length_zebra_path is required for PARTIAL_COLUMN_ZEBRA_PATTERN";
        }
        if (!VALID_COLUMN_LENGTHS.includes(row[CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA])) {
            return "Row " + row._rowNumber + ": Invalid pick_column_length_zebra_path \"" + row[CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA] + "\". Must be 3, 4, or 5";
        }
    }

    // Sideline: included_sort_zones required and max 4
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.SIDELINE_BLOCK_FILL) {
        if (!row[CSV_HEADERS.INCLUDED_SORT_ZONES]) {
            return "Row " + row._rowNumber + ": included_sort_zones is required for SIDELINE_BLOCK_FILL";
        }
        var zones = toArray(row[CSV_HEADERS.INCLUDED_SORT_ZONES]);
        if (zones.length > MAX_SIDELINE_SORT_ZONES) {
            return "Row " + row._rowNumber + ": included_sort_zones must have at most " + MAX_SIDELINE_SORT_ZONES + " entries for SIDELINE";
        }
    }

    // SAME_DAY: sort location order required
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL) {
        if (!row[CSV_HEADERS.FILL_SORT_LOCATION_ORDER]) {
            return "Row " + row._rowNumber + ": fill_sort_location_order is required for SAME_DAY_STANDARD_FILL";
        }
    }
    if (row[CSV_HEADERS.PICK_ALGORITHM] === SORT_PATH_PICK_ALGORITHMS.SAME_DAY_STANDARD_PATH) {
        if (!row[CSV_HEADERS.PICK_SORT_LOCATION_ORDER]) {
            return "Row " + row._rowNumber + ": pick_sort_location_order is required for SAME_DAY_STANDARD_PATH";
        }
    }

    return null;
}

// --- Utility functions ---

function toArray(str) {
    if (!str) return [];
    return str.split(";").map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 0; });
}

function buildClusterParams(clusterParams) {
    if (!clusterParams || clusterParams.length === 0) {
        return { blockLength: [], maxClusterCapacityForNode: [], adtaClusterIdentifierForNode: [] };
    }

    var blockLength = clusterParams
        .filter(function(cp) { return cp.blockLength != null; })
        .map(function(cp) { return { clusterName: cp.clusterName, clusterParameterValue: String(cp.blockLength) }; });

    var maxClusterCapacityForNode = clusterParams
        .filter(function(cp) { return cp.maxCapacity != null; })
        .map(function(cp) { return { clusterName: cp.clusterName, clusterParameterValue: String(cp.maxCapacity) }; });

    var adtaClusterIdentifierForNode = clusterParams
        .filter(function(cp) { return cp.adtaEnabled; })
        .map(function(cp) { return { clusterName: cp.clusterName, clusterParameterValue: "1" }; });

    return { blockLength: blockLength, maxClusterCapacityForNode: maxClusterCapacityForNode, adtaClusterIdentifierForNode: adtaClusterIdentifierForNode };
}

function buildClusterSequenceForNode(clusterParams) {
    if (!clusterParams || clusterParams.length === 0) return null;
    return clusterParams.map(function(cp, index) {
        return { clusterName: cp.clusterName, clusterParameterValue: String(index + 1) };
    });
}

// --- Fill path builders per algorithm ---

function buildFillAdditionalFields(row, clusterParams) {
    var fillAlgo = row[CSV_HEADERS.FILL_ALGORITHM];

    switch (fillAlgo) {
        case SORT_PATH_FILL_ALGORITHMS.STANDARD:
        case SORT_PATH_FILL_ALGORITHMS.STANDARD_BLOCK: {
            var cp = buildClusterParams(clusterParams);
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
                },
                aisleOrder: {
                    type: row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
                },
                adtaPrioritizationOrder: row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] || null,
                deprioritizedTiers: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS]),
                deprioritizedTiersForADTAClusters: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS_ADTA]),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: (row[CSV_HEADERS.BALANCING_SORT_ZONES] || "").toLowerCase() === "true",
                    optimiseClusterUsage: (row[CSV_HEADERS.OPTIMISE_CLUSTER_USAGE] || "").toLowerCase() !== "false",
                },
                maxClusterCapacityForNode: cp.maxClusterCapacityForNode,
                adtaClusterIdentifierForNode: cp.adtaClusterIdentifierForNode,
                blockLength: cp.blockLength,
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.COMBINATRIX: {
            return {
                fingerOrder: {
                    type: row[CSV_HEADERS.FILL_FINGER_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
                aisleOrder: {
                    type: row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
                },
                adtaPrioritizationOrder: row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] || null,
                deprioritizedTiers: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS]),
                deprioritizedTiersForADTAClusters: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS_ADTA]),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: (row[CSV_HEADERS.BALANCING_SORT_ZONES] || "").toLowerCase() === "true",
                    optimiseClusterUsage: (row[CSV_HEADERS.OPTIMISE_CLUSTER_USAGE] || "").toLowerCase() !== "false",
                },
                splitPercentage: row[CSV_HEADERS.FILL_SPLIT_PERCENTAGE] || null,
                maxClusterCapacityForNode: [],
                adtaClusterIdentifierForNode: [],
                blockLength: [],
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.CLUSTER_WISE_STANDARD: {
            var cpCw = buildClusterParams(clusterParams);
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
                },
                aisleOrder: {
                    type: row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
                },
                adtaPrioritizationOrder: row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] || null,
                deprioritizedTiers: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS]),
                deprioritizedTiersForADTAClusters: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS_ADTA]),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: (row[CSV_HEADERS.BALANCING_SORT_ZONES] || "").toLowerCase() === "true",
                    optimiseClusterUsage: (row[CSV_HEADERS.OPTIMISE_CLUSTER_USAGE] || "").toLowerCase() !== "false",
                },
                maxClusterCapacityForNode: cpCw.maxClusterCapacityForNode,
                adtaClusterIdentifierForNode: cpCw.adtaClusterIdentifierForNode,
                blockLength: cpCw.blockLength,
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.FLOOR_LOCATION_STANDARD_FILL_PATH: {
            var cpStag = buildClusterParams(clusterParams);
            var clusterSeqForNode = buildClusterSequenceForNode(clusterParams);
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || SortingOrder.CUSTOM,
                    customSequence: toArray(row[CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
                    clusterSequenceForNode: clusterSeqForNode,
                },
                aisleOrder: {
                    type: row[CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
                },
                adtaPrioritizationOrder: row[CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] || null,
                deprioritizedTiers: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS]),
                deprioritizedTiersForADTAClusters: toArray(row[CSV_HEADERS.FILL_DEPRIORITIZED_TIERS_ADTA]),
                scienceModelOptimizationParameter: {
                    balancingSortZonesInClusters: (row[CSV_HEADERS.BALANCING_SORT_ZONES] || "").toLowerCase() === "true",
                    optimiseClusterUsage: (row[CSV_HEADERS.OPTIMISE_CLUSTER_USAGE] || "").toLowerCase() !== "false",
                },
                maxClusterCapacityForNode: cpStag.maxClusterCapacityForNode,
                adtaClusterIdentifierForNode: cpStag.adtaClusterIdentifierForNode,
                blockLength: cpStag.blockLength,
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.SIDELINE_BLOCK_FILL: {
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
                },
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL: {
            return {
                sortLocationOrder: {
                    type: row[CSV_HEADERS.FILL_SORT_LOCATION_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
            };
        }
        default:
            return {};
    }
}

// --- Pick path builders ---

function buildPickAdditionalFields(row) {
    var pickAlgo = row[CSV_HEADERS.PICK_ALGORITHM];

    switch (pickAlgo) {
        case SORT_PATH_PICK_ALGORITHMS.SAME_DAY_STANDARD_PATH: {
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] || null,
                    customSequence: toArray(row[CSV_HEADERS.PICK_CLUSTER_CUSTOM_SEQUENCE]),
                },
                sortLocationOrder: {
                    type: row[CSV_HEADERS.PICK_SORT_LOCATION_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
            };
        }
        case SORT_PATH_PICK_ALGORITHMS.FLOOR_LOCATION_STANDARD_PICK_PATH:
        case SORT_PATH_PICK_ALGORITHMS.SIDELINE_ZEBRA_PATH: {
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.PICK_CLUSTER_CUSTOM_SEQUENCE]),
                },
                columnSplitRatio: row[CSV_HEADERS.PICK_COLUMN_SPLIT_RATIO] || "0.5",
            };
        }
        default: {
            // Regular pick algorithms (NO_ZEBRA, ALTERNATE_AISLE, ONE_AISLE_SKIPPING, TWO_AISLE_SKIPPING, PARTIAL_COLUMN)
            return {
                clusterOrder: {
                    type: row[CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.PICK_CLUSTER_CUSTOM_SEQUENCE]),
                },
                aisleOrder: {
                    type: row[CSV_HEADERS.PICK_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[CSV_HEADERS.PICK_AISLE_CUSTOM_SEQUENCE]),
                },
                columnLengthForZebraPath: row[CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA] || null,
                columnSplitRatio: row[CSV_HEADERS.PICK_COLUMN_SPLIT_RATIO] || "0.5",
            };
        }
    }
}

/**
 * Looks up settingId for a given nodeId+cycle from existing configurations.
 */
export function lookupSettingId(nodeId, cycle, existingConfigs) {
    if (!existingConfigs || existingConfigs.length === 0) return null;
    var match = existingConfigs.find(function(config) {
        return config.nodeId === nodeId && config.cycle === cycle;
    });
    return match ? match.settingId : null;
}

/**
 * Converts a validated CSV row into a sortPathSetting payload.
 */
export function buildSortPathSettingFromRow(row, clusterParamsMap, existingConfigs) {
    var nodeId = row[CSV_HEADERS.NODE_ID];
    var cycle = row[CSV_HEADERS.CYCLE];
    var key = nodeId + "#" + cycle;
    var clusterParams = clusterParamsMap ? clusterParamsMap[key] : null;
    var settingId = lookupSettingId(nodeId, cycle, existingConfigs);

    // Auto-generate name: cycle_algorithm, truncate if needed
    var name = cycle + "_" + row[CSV_HEADERS.FILL_ALGORITHM];
    if (name.length > MAX_NAME_LENGTH) {
        name = cycle;
    }

    var fillAlgo = row[CSV_HEADERS.FILL_ALGORITHM];

    return {
        settingId: settingId,
        name: name,
        cycle: cycle,
        nodeId: nodeId,
        binType: row[CSV_HEADERS.BIN_TYPE] || null,
        volumeType: null,
        cycleList: null,
        injectionType: null,
        inductLocationType: null,
        status: SORT_PATH_SETTING_STATUS.UPDATED,
        additionalParams: null,
        excludedLocations: {
            excludedClusters: toArray(row[CSV_HEADERS.EXCLUDED_CLUSTERS]),
            excludedAisles: toArray(row[CSV_HEADERS.EXCLUDED_AISLES]),
            excludedBins: toArray(row[CSV_HEADERS.EXCLUDED_BINS]),
            excludedFloorLocations: [],
        },
        includedLocations: {
            includedAisles: toArray(row[CSV_HEADERS.INCLUDED_AISLES]),
            includedColumns: toArray(row[CSV_HEADERS.INCLUDED_COLUMNS]),
            includedSortZones: toArray(row[CSV_HEADERS.INCLUDED_SORT_ZONES]),
            includedSortLocations: [],
        },
        fillPathSetting: {
            algorithm: fillAlgo,
            additionalFields: buildFillAdditionalFields(row, clusterParams),
        },
        pickPathSetting: {
            algorithm: row[CSV_HEADERS.PICK_ALGORITHM],
            additionalFields: buildPickAdditionalFields(row),
        },
    };
}
